import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";

const FIELD_TYPES = [
  { v: "short_text", label: "Short text" },
  { v: "long_text", label: "Long text" },
  { v: "number", label: "Number" },
  { v: "date", label: "Date" },
  { v: "dropdown", label: "Dropdown" },
  { v: "radio", label: "Radio" },
  { v: "checkbox", label: "Checkbox (yes/no)" },
  { v: "file", label: "File upload" },
];

const EMPTY = { name: "", field_type: "short_text", applies_to: "event", event_type_id: "", required: false, help_text: "", options: [], display_order: 0, show_on_invoice: false };

export default function CustomFields() {
  const [eventTypes, setEventTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("event");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [optionDraft, setOptionDraft] = useState("");
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(12);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const load = async () => {
    const [et, cf] = await Promise.all([
      api.get("/event-types"),
      api.get("/custom-fields", { params: { applies_to: tab, page, per_page: perPage } }),
    ]);
    setEventTypes(et.data);
    setItems(Array.isArray(cf.data) ? cf.data : cf.data.items || []);
    setTotal(Array.isArray(cf.data) ? cf.data.length : cf.data.total || 0);
    setPages(Array.isArray(cf.data) ? 1 : cf.data.pages || 1);
  };
  useEffect(() => { load(); }, [page, tab]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, applies_to: tab };
      if (tab === "event" && !form.event_type_id) { toast.error("Pick an event type"); return; }
      if (tab === "client") payload.event_type_id = null;
      if (editing) {
        await api.patch(`/custom-fields/${editing}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/custom-fields", payload);
        toast.success("Field created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this field?")) return;
    await api.delete(`/custom-fields/${id}`);
    load();
  };

  const filtered = items;

  return (
    <div className="space-y-6" data-testid="custom-fields-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Configuration</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Custom fields</h1>
          <p className="text-sm text-zinc-600 mt-1">Capture exactly what matters — per event type, or per client.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); setOptionDraft(""); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A1A1A] text-white hover:bg-black rounded-full" data-testid="custom-field-add-btn"><Plus size={16} className="mr-1" /> Add field</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit field" : "New custom field"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="custom-field-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Field name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Guest Count" data-testid="cf-name-input" /></div>
                <div><Label>Type</Label>
                  <Select value={form.field_type} onValueChange={(v) => setForm({ ...form, field_type: v })}>
                    <SelectTrigger data-testid="cf-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {tab === "event" && (
                <div>
                  <Label>Event type</Label>
                  <Select value={form.event_type_id} onValueChange={(v) => setForm({ ...form, event_type_id: v })}>
                    <SelectTrigger data-testid="cf-event-type-select"><SelectValue placeholder="Pick event type" /></SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((et) => <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(form.field_type === "dropdown" || form.field_type === "radio") && (
                <div>
                  <Label>Options</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={optionDraft} onChange={(e) => setOptionDraft(e.target.value)} placeholder="Add an option" data-testid="cf-option-input" />
                    <Button type="button" variant="outline" onClick={() => { if (optionDraft.trim()) { setForm({ ...form, options: [...(form.options || []), optionDraft.trim()] }); setOptionDraft(""); } }} data-testid="cf-option-add-btn">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(form.options || []).map((o, i) => (
                      <span key={i} className="text-xs bg-zinc-100 px-2 py-1 rounded-full flex items-center gap-1">
                        {o}
                        <button type="button" onClick={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div><Label>Help text</Label><Input value={form.help_text} onChange={(e) => setForm({ ...form, help_text: e.target.value })} /></div>
              <div className="flex items-center gap-3">
                <Switch checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: !!v })} data-testid="cf-required-switch" />
                <span className="text-sm">Required</span>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!form.show_on_invoice} onCheckedChange={(v) => setForm({ ...form, show_on_invoice: !!v })} data-testid="cf-show-on-invoice-switch" />
                <span className="text-sm">Show this field on invoices (above Terms & Conditions)</span>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="cf-save-btn">{editing ? "Save" : "Create field"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={(value) => { setTab(value); setPage(1); }}>
        <TabsList>
          <TabsTrigger value="event" data-testid="cf-tab-event">Event fields</TabsTrigger>
          <TabsTrigger value="client" data-testid="cf-tab-client">Client fields</TabsTrigger>
        </TabsList>
        <TabsContent value="event" className="mt-6">
          <FieldList items={filtered} eventTypes={eventTypes} onDelete={del} onEdit={(f) => { setForm({ ...f, options: f.options || [] }); setEditing(f.id); setOpen(true); }} />
        </TabsContent>
        <TabsContent value="client" className="mt-6">
          <FieldList items={filtered} eventTypes={eventTypes} onDelete={del} onEdit={(f) => { setForm({ ...f, options: f.options || [] }); setEditing(f.id); setOpen(true); }} />
        </TabsContent>
      </Tabs>

      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">Showing {items.length ? (page - 1) * perPage + 1 : 0} - {(page - 1) * perPage + items.length} of {total}</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-medium">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FieldList({ items, eventTypes, onDelete, onEdit }) {
  if (items.length === 0)
    return <Card className="bg-white border-black/10"><CardContent className="p-10 text-center text-sm text-zinc-500">No fields yet.</CardContent></Card>;
  return (
    <Card className="bg-white border-black/10">
      <CardContent className="p-0">
        <ul className="divide-y divide-black/5">
          {items.map((f) => {
            const et = eventTypes.find((e) => e.id === f.event_type_id);
            return (
              <li key={f.id} className="p-4 flex items-center justify-between" data-testid={`cf-row-${f.id}`}>
                <div>
                  <div className="text-sm font-medium">{f.name}{f.required && <span className="text-red-600">*</span>}</div>
                  <div className="text-xs text-zinc-500">{f.field_type.replace("_", " ")}{et && ` · ${et.name}`}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(f)} data-testid={`cf-edit-${f.id}`}>Edit</Button>
                  <Button variant="ghost" size="icon" className="text-red-700 hover:bg-red-50" onClick={() => onDelete(f.id)} data-testid={`cf-delete-${f.id}`}><Trash2 size={14} /></Button>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
